import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Volume2 } from "lucide-react";
import { getOnThisDay } from "@/lib/iconic-dates";
import { postTimeJump, fetchHistoricalMedia } from "@/lib/api";

/**
 * LandingTrailer — silent Ken-Burns slideshow behind the hero.
 *
 * Shows 3–4 Wikipedia Commons photos from today's iconic historical date
 * (matched by month/day). Auto-rotates daily via `getOnThisDay()`.
 * Falls back to a still colour block if no photos are available yet — the
 * component never renders as broken.
 *
 * Sizing: absolute-positioned inside a parent `relative` container.
 */
export default function LandingTrailer() {
  const [images, setImages] = useState([]);
  const [snapshotId, setSnapshotId] = useState(null);
  const [entry, setEntry] = useState(null);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  // Pick today's iconic entry once — memoized so we don't re-request on every render.
  const todayEntry = useMemo(() => {
    const { entries, todayLabel } = getOnThisDay();
    if (!entries || entries.length === 0) return null;
    return { ...entries[0], todayLabel };
  }, []);

  useEffect(() => {
    if (!todayEntry) return;
    let cancelled = false;
    (async () => {
      try {
        setEntry(todayEntry);
        const snap = await postTimeJump({ date: todayEntry.date, region: todayEntry.region });
        if (cancelled) return;
        setSnapshotId(snap.id);
        const media = await fetchHistoricalMedia(snap.id);
        if (cancelled) return;
        const urls = (media?.images || [])
          .map((im) => im.thumb_url || im.full_url)
          .filter(Boolean)
          .slice(0, 4);
        setImages(urls);
      } catch {
        // Silent — the component just falls back to the still block.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayEntry?.date, todayEntry?.region]);

  // Auto-advance every 6s
  useEffect(() => {
    if (images.length < 2) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [images.length]);

  if (!todayEntry) return null;

  const label = entry?.label || todayEntry.label;
  const dateLabel = entry?.todayLabel || todayEntry.todayLabel;
  const yearsAgo = entry?.yearsAgo;
  const year = (entry?.date || todayEntry.date).slice(0, 4);
  const hasImages = images.length > 0;

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none z-0"
      data-testid="landing-trailer"
    >
      {/* Photo layers with Ken-Burns */}
      {hasImages ? (
        images.map((src, i) => (
          <div
            key={src}
            aria-hidden="true"
            className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
            style={{
              opacity: i === idx ? 1 : 0,
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(30%) contrast(1.05) brightness(0.72)",
              transform: i === idx ? "scale(1.08)" : "scale(1)",
              transformOrigin: i % 2 === 0 ? "left top" : "right bottom",
              transition: "opacity 1400ms ease-in-out, transform 8000ms ease-out",
            }}
          />
        ))
      ) : (
        // Fallback — subtle radial gradient in press-red so the trailer strip never looks broken
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 40%, rgba(186,63,29,0.35), rgba(26,28,32,0.9) 60%), #1A1C20",
          }}
        />
      )}

      {/* Dark overlay for text legibility over the hero */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(250,247,242,0.6) 0%, rgba(250,247,242,0.35) 50%, rgba(250,247,242,0.05) 100%)",
        }}
      />
      {/* Subtle grain */}
      <div aria-hidden="true" className="absolute inset-0 opacity-10 mix-blend-multiply paper-grain" />

      {/* Date-label ribbon (bottom-right of hero) — this element IS interactive */}
      <div
        className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 pointer-events-auto flex flex-col items-end gap-2 max-w-[280px] sm:max-w-xs"
        data-testid="landing-trailer-caption"
      >
        <div className="bg-ink text-paper border-2 border-ink px-3 py-2 shadow-hard-sm text-right">
          <div className="font-mono uppercase tracking-[0.25em] text-[9px] text-paper/60 mb-0.5">
            Today · {dateLabel} · {year}
          </div>
          <div className="font-headline text-base sm:text-lg leading-tight uppercase tracking-tight">
            {label}
          </div>
          {yearsAgo ? (
            <div className="font-mono text-[10px] text-paper/60 mt-1">
              {yearsAgo} years ago
            </div>
          ) : null}
        </div>
        {snapshotId ? (
          <Link
            to={`/watch/${snapshotId}`}
            className="inline-flex items-center gap-1.5 bg-press-red text-paper border-2 border-ink px-3 py-2 shadow-hard-sm font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-ink"
            data-testid="landing-trailer-watch-btn"
          >
            <Play size={12} /> Watch with sound →
          </Link>
        ) : null}
      </div>
    </div>
  );
}
